import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BadgeCheck, CircleAlert, Star } from 'lucide-react';
import { canReview, createReview, getProductReviews } from '../../../services/reviewService';
import { getErrorMessage } from '../../../utils/apiError';
import { formatDate } from '../../../utils/format';
import { SESSION_EVENT, hasSession, loginPath } from '../../../utils/session';
import { Rating, toast } from '../../../ui';

const MIN_COMMENT = 3;
const MAX_COMMENT = 2000;
const INITIAL_VISIBLE = 5;

const newestFirst = (a, b) =>
  new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime();

/**
 * Reviews for a product: { status: 'loading'|'ready'|'error', items (newest first), error,
 * average, count, reload, retry }. reload() refreshes quietly; retry() shows the loading state.
 */
export const useProductReviews = productId => {
  const [state, setState] = useState({ status: 'loading', items: [], error: null });
  const [reloadKey, setReloadKey] = useState(0);

  // A different product starts from a clean slate.
  useEffect(() => {
    setState({ status: 'loading', items: [], error: null });
  }, [productId]);

  useEffect(() => {
    if (!productId) return undefined;
    let active = true;
    getProductReviews(productId)
      .then(body => {
        if (!active) return;
        const items = (Array.isArray(body?.data) ? body.data : []).slice().sort(newestFirst);
        setState({ status: 'ready', items, error: null });
      })
      .catch(error => {
        if (!active) return;
        setState({
          status: 'error',
          items: [],
          error: getErrorMessage(error, "We couldn't load the reviews."),
        });
      });
    return () => {
      active = false;
    };
  }, [productId, reloadKey]);

  const reload = useCallback(() => setReloadKey(k => k + 1), []);
  const retry = useCallback(() => {
    setState(prev => ({ ...prev, status: 'loading', error: null }));
    setReloadKey(k => k + 1);
  }, []);

  return useMemo(() => {
    const ratings = state.items.map(r => Number(r?.rating) || 0).filter(r => r > 0);
    const count = state.items.length;
    const average = ratings.length
      ? Math.round((ratings.reduce((sum, r) => sum + r, 0) / ratings.length) * 10) / 10
      : 0;
    return { ...state, count, average, reload, retry };
  }, [state, reload, retry]);
};

// Re-renders when the user signs in or out in another part of the app (or another tab).
const useSignedIn = () => {
  const [signedIn, setSignedIn] = useState(hasSession);
  useEffect(() => {
    const sync = () => setSignedIn(hasSession());
    window.addEventListener(SESSION_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(SESSION_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  return signedIn;
};

const StarInput = ({ value, onChange, name, invalid, errorId }) => {
  const [hover, setHover] = useState(0);
  const shown = hover || value;

  return (
    <div className="pd-stars" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map(n => (
        <label
          key={n}
          className={`pd-star${n <= shown ? ' is-on' : ''}`}
          onMouseEnter={() => setHover(n)}
        >
          <input
            type="radio"
            className="sr-only"
            name={name}
            value={n}
            checked={value === n}
            onChange={() => onChange(n)}
            aria-describedby={invalid ? errorId : undefined}
          />
          <Star aria-hidden="true" />
          <span className="sr-only">
            {n} {n === 1 ? 'star' : 'stars'}
          </span>
        </label>
      ))}
    </div>
  );
};

const ReviewForm = ({ productId, onPosted, onCancel }) => {
  const uid = useId().replace(/:/g, '');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef(null);
  const commentRef = useRef(null);

  const ids = {
    rating: `${uid}-rating-error`,
    comment: `${uid}-comment`,
    hint: `${uid}-comment-hint`,
    commentError: `${uid}-comment-error`,
  };

  useEffect(() => {
    // Start on the first star when the form opens.
    formRef.current?.querySelector('input[type="radio"]')?.focus();
  }, []);

  const handleSubmit = async event => {
    event.preventDefault();
    const text = comment.trim();
    const next = {};
    if (rating < 1 || rating > 5) next.rating = 'Choose a rating from 1 to 5 stars.';
    if (text.length < MIN_COMMENT) next.comment = `Write at least ${MIN_COMMENT} characters.`;
    else if (text.length > MAX_COMMENT) next.comment = 'Keep your review under 2,000 characters.';
    setErrors(next);
    setSubmitError('');

    if (next.rating) {
      formRef.current?.querySelector('input[type="radio"]')?.focus();
      return;
    }
    if (next.comment) {
      commentRef.current?.focus();
      return;
    }

    setSubmitting(true);
    try {
      const body = await createReview({ productId, rating, comment: text });
      if (body && body.success === false) {
        throw Object.assign(new Error(body.message || 'Review not saved'), {
          response: { data: body },
        });
      }
      toast.success('Thanks, your review is posted.');
      onPosted();
    } catch (error) {
      const message = getErrorMessage(error, "We couldn't post your review. Please try again.");
      setSubmitError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form ref={formRef} className="pk-form pd-review-form" onSubmit={handleSubmit} noValidate>
      <fieldset className="pd-review-form__stars">
        <legend className="pk-label">Your rating</legend>
        <StarInput
          name={`${uid}-rating`}
          value={rating}
          onChange={value => {
            setRating(value);
            setErrors(prev => ({ ...prev, rating: undefined }));
          }}
          invalid={Boolean(errors.rating)}
          errorId={ids.rating}
        />
        {errors.rating && (
          <p className="pk-error" id={ids.rating}>
            {errors.rating}
          </p>
        )}
      </fieldset>

      <div className="pk-field">
        <label className="pk-label" htmlFor={ids.comment}>
          Your review
        </label>
        <textarea
          ref={commentRef}
          id={ids.comment}
          className="pk-textarea"
          rows={5}
          maxLength={MAX_COMMENT}
          placeholder="What did you build with it? How was the setup?"
          value={comment}
          onChange={e => {
            setComment(e.target.value);
            if (errors.comment) setErrors(prev => ({ ...prev, comment: undefined }));
          }}
          aria-invalid={Boolean(errors.comment) || undefined}
          aria-describedby={errors.comment ? `${ids.hint} ${ids.commentError}` : ids.hint}
        />
        <div className="pd-review-form__meta">
          <span id={ids.hint} className="pk-hint">
            {MIN_COMMENT}–{MAX_COMMENT.toLocaleString('en-IN')} characters
          </span>
          <span className="pk-hint">
            {comment.length.toLocaleString('en-IN')} / {MAX_COMMENT.toLocaleString('en-IN')}
          </span>
        </div>
        {errors.comment && (
          <p className="pk-error" id={ids.commentError}>
            {errors.comment}
          </p>
        )}
      </div>

      {submitError && (
        <div className="pk-alert" data-tone="danger" role="alert">
          <CircleAlert aria-hidden="true" />
          <span>{submitError}</span>
        </div>
      )}

      <div className="pk-cluster">
        <button
          type="submit"
          className="pk-btn pk-btn--primary pk-btn--sm"
          disabled={submitting}
          aria-busy={submitting || undefined}
        >
          Post review
        </button>
        <button
          type="button"
          className="pk-btn pk-btn--ghost pk-btn--sm"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

// Who may write a review: signed-out visitors get a sign-in link, signed-in users are
// checked with the server (only verified buyers who haven't reviewed yet).
const ReviewComposer = ({ productId, onPosted }) => {
  const location = useLocation();
  const signedIn = useSignedIn();
  const [eligibility, setEligibility] = useState({ status: 'idle', reason: null });
  const [open, setOpen] = useState(false);
  const toggleRef = useRef(null);
  const formId = `pd-review-form-${productId}`;

  useEffect(() => {
    setOpen(false);
    if (!signedIn || !productId) {
      setEligibility({ status: 'idle', reason: null });
      return undefined;
    }
    let active = true;
    setEligibility({ status: 'checking', reason: null });
    canReview(productId).then(result => {
      if (!active) return;
      setEligibility(
        result.canReview
          ? { status: 'allowed', reason: null }
          : { status: 'denied', reason: result.reason || "You can't review this project." }
      );
    });
    return () => {
      active = false;
    };
  }, [signedIn, productId]);

  if (!signedIn) {
    return (
      <p className="pd-reviews__note">
        Bought this project?{' '}
        <Link className="pk-link" to={loginPath(`${location.pathname}${location.search}`)}>
          Sign in to review
        </Link>
      </p>
    );
  }

  if (eligibility.status === 'checking' || eligibility.status === 'idle') {
    return (
      <p className="pd-reviews__note">
        <span className="pk-skel pd-reviews__note-skel" aria-hidden="true" />
        <span className="sr-only">Checking whether you can review this project</span>
      </p>
    );
  }

  if (eligibility.status === 'posted') {
    return (
      <p className="pd-reviews__note" role="status">
        Thanks for your review.
      </p>
    );
  }

  if (eligibility.status === 'denied') {
    return <p className="pd-reviews__note">{eligibility.reason}</p>;
  }

  return (
    <div className="pd-reviews__compose">
      {!open ? (
        <button
          ref={toggleRef}
          type="button"
          className="pk-btn pk-btn--secondary pk-btn--sm"
          aria-expanded="false"
          aria-controls={formId}
          onClick={() => setOpen(true)}
        >
          Write a review
        </button>
      ) : (
        <div id={formId}>
          <ReviewForm
            productId={productId}
            onCancel={() => {
              setOpen(false);
              // Hand focus back to the button that opened the form.
              setTimeout(() => toggleRef.current?.focus(), 0);
            }}
            onPosted={() => {
              setOpen(false);
              setEligibility({ status: 'posted', reason: null });
              onPosted();
            }}
          />
        </div>
      )}
    </div>
  );
};

const ReviewList = ({ items }) => {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? items : items.slice(0, INITIAL_VISIBLE);

  return (
    <>
      <ul className="pd-review-list">
        {visible.map((review, i) => (
          <li key={review.id || i} className="pd-review">
            <div className="pd-review__head">
              <Rating disabled allowHalf size="small" value={Number(review.rating) || 0} />
              {review.isVerifiedPurchase && (
                <span className="pk-badge" data-tone="success">
                  <BadgeCheck aria-hidden="true" />
                  Verified purchase
                </span>
              )}
            </div>
            <p className="pd-review__meta">
              <span className="pd-review__name">{review.userName || 'Customer'}</span>
              <span aria-hidden="true"> · </span>
              <time dateTime={review.createdAt || undefined}>{formatDate(review.createdAt)}</time>
            </p>
            {review.comment && <p className="pd-review__text">{review.comment}</p>}
          </li>
        ))}
      </ul>
      {items.length > INITIAL_VISIBLE && (
        <button
          type="button"
          className="pk-btn pk-btn--secondary pk-btn--sm pd-review-list__more"
          aria-expanded={showAll}
          onClick={() => setShowAll(v => !v)}
        >
          {showAll ? 'Show fewer reviews' : `Show all ${items.length} reviews`}
        </button>
      )}
    </>
  );
};

/** Reviews block for the product page. `reviews` comes from useProductReviews(productId). */
const ProductReviewSection = ({ productId, reviews }) => {
  const { status, items, error, count, average } = reviews;

  return (
    <section
      className="pd-section pd-reviews pd-stars-read"
      id="reviews"
      tabIndex={-1}
      aria-labelledby="pd-reviews-title"
    >
      <h2 id="pd-reviews-title" className="pd-section__title">
        Reviews
      </h2>

      {status === 'ready' && count > 0 && (
        <div className="pd-reviews__summary">
          <span className="pd-reviews__avg">{average.toFixed(1)}</span>
          <div>
            <Rating disabled allowHalf value={average} />
            <p className="pd-reviews__based">
              Based on {count} {count === 1 ? 'review' : 'reviews'}
            </p>
          </div>
        </div>
      )}

      <ReviewComposer productId={productId} onPosted={reviews.reload} />

      {status === 'loading' && (
        <div className="pd-review-list" aria-hidden="true">
          {[0, 1].map(i => (
            <div key={i} className="pd-review">
              <span className="pk-skel pd-skel-line pd-skel-line--short" />
              <span className="pk-skel pd-skel-line" />
              <span className="pk-skel pd-skel-line" />
            </div>
          ))}
        </div>
      )}

      {status === 'error' && (
        <div className="pk-alert pd-reviews__error" data-tone="danger" role="alert">
          <CircleAlert aria-hidden="true" />
          <span>{error}</span>
          <button type="button" className="pk-btn pk-btn--secondary pk-btn--xs" onClick={reviews.retry}>
            Retry
          </button>
        </div>
      )}

      {status === 'ready' && count === 0 && (
        <p className="pd-reviews__empty">No reviews yet.</p>
      )}

      {status === 'ready' && count > 0 && <ReviewList key={productId} items={items} />}
    </section>
  );
};

export default ProductReviewSection;
